/* eslint-disable no-restricted-imports */
import { useBasicCheckOut } from '@/data/Content/BasicCheckOut';
import { calculateProductPriceIncrease } from '@/data/Content/BasicCheckOut/utils/calculateProductPriceIncrease';
import { extractCodesByProductKeyword } from '@/data/Content/BasicCheckOut/utils/extractCodesByProductKeyword';
import { generateOrderWhatsAppMessage } from '@/data/Content/BasicCheckOut/utils/generateOrderWhatsAppMessage';
import { groupAndSumProducts } from '@/data/Content/BasicCheckOut/utils/groupAndSumProducts';
import { useCart } from '@/data/Content/Cart';
import { useProductDetailsAddToCart } from '@/data/Content/ProductDetails';
import { ProductType } from '@/data/types/Product';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { useFlag } from 'presentation/hooks/useFlag';
import { useCallback, useMemo } from 'react';

export interface Product {
	name: string;
	price: number;
	code: string;
	thumbnail: string;
	size: string;
	href: string | undefined;
	partNumber: string;
	onRemove: () => Promise<void>;
	onAdd: () => Promise<void>;
}

export interface ProductGroup extends Product {
	quantity: number;
}

interface RenderProps {
	products: ProductGroup[];
	productsGroup: ProductGroup[];
	grandTotal: number;
	loading: boolean;
	loadingAction: boolean;
	onSharedForWhatsApp: () => void;
	onEmptyCart: () => Promise<void>;
	removeAll: (partNumber: string) => Promise<void>;
}

interface Props {
	order: ReturnType<typeof useCart>['data'];
	children: (args: RenderProps) => JSX.Element;
	onDeleteCart: () => Promise<void>;
	onClose: () => void;
}

type OnChangeQuantity = (quantity: number | null) => Promise<void>;

export const CartSetupRender = ({ order, children, onDeleteCart, onClose }: Props) => {
	const { orderItemListRowsSorted, loadingOrderItemListRows } = useBasicCheckOut({
		orderItem: order?.orderItem || [],
		orderId: order?.orderId || '',
	});

	const { addToCartByProduct } = useProductDetailsAddToCart();

	const { flag: loadingAction, ...restFlagAction } = useFlag();

	const removeProductBuilder = useCallback(
		(onChange: OnChangeQuantity) => async () => {
			restFlagAction.onTurnOn();
			await onChange(0);
			restFlagAction.onTurnOff();
		},
		[restFlagAction]
	);

	const addProductBuilder = useCallback(
		(product: ProductType) => async () => {
			restFlagAction.onTurnOn();
			await addToCartByProduct(product);
			restFlagAction.onTurnOff();
		},
		[addToCartByProduct, restFlagAction]
	);

	const { products, productsGroup, grandTotal } = useMemo(() => {
		const products = orderItemListRowsSorted.map((product) => {
			const isElectronics =
				findProductAttribute(product.details.attributes, 'Uso')?.value === 'Electronicos';

			return {
				name: product.details.name,
				price: isElectronics ? 0 : calculateProductPriceIncrease(product.details.prices.list || 0),
				code: extractCodesByProductKeyword(product.product.keyword).model,
				thumbnail: product.details.thumbnail,
				size: findProductAttribute(product.details.attributes, 'Talla')?.value || 'Unknown',
				href: product.details.seo?.href,
				quantity: 1,
				partNumber: product.details.partNumber,
				onRemove: removeProductBuilder(product.orderItemListRow.quantity.onChange),
				onAdd: addProductBuilder(product.product),
			};
		});

		const productsGroup = groupAndSumProducts(products) as ProductGroup[];

		const grandTotal = products.reduce((acc, product) => acc + product.price, 0);

		return {
			products,
			productsGroup,
			grandTotal,
		};
	}, [orderItemListRowsSorted, removeProductBuilder, addProductBuilder]);

	const onSharedForWhatsApp = useCallback(() => {
		const message = generateOrderWhatsAppMessage(productsGroup);
		const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
		window.open(url, '_blank');
	}, [productsGroup]);

	const onEmptyCart = useCallback(async () => {
		restFlagAction.onTurnOn();
		await onDeleteCart();
		onClose();
		restFlagAction.onTurnOff();
	}, [onDeleteCart, onClose, restFlagAction]);

	const removeAll = useCallback(
		async (partNumber: string) => {
			restFlagAction.onToggle();
			const itemsToRemove = products.filter((product) => product.partNumber === partNumber);
			await Promise.all(itemsToRemove.map((item) => item.onRemove()));
			restFlagAction.onTurnOff();
		},
		[products, restFlagAction]
	);

	return children({
		products,
		productsGroup,
		grandTotal,
		loading: loadingOrderItemListRows,
		loadingAction,
		onSharedForWhatsApp,
		onEmptyCart,
		removeAll,
	});
};
